import React from 'react';
import PropTypes from 'prop-types';
import './LoadingSystem.css';

/**
 * Skeleton - Marcador de posición animado para contenido que está cargando
 * 
 * Casos de uso:
 * - Carga de listas de elementos (cards, items, filas de tabla)
 * - Precarga de layouts antes de que lleguen los datos
 * - Mantener la estructura visual mientras se carga contenido
 * 
 * Mejores prácticas según Material Design y mejores UX:
 * - Usar skeletons que coincidan con la forma del contenido final
 * - Mantener el tamaño y posición del contenido real
 * - Combinar múltiples skeletons para representar layouts complejos
 * 
 * @example
 * // Skeleton simple
 * <Skeleton width="100%" height="20px" />
 * 
 * @example
 * // Card skeleton
 * <div className="card">
 *   <Skeleton variant="circular" width="40px" height="40px" />
 *   <Skeleton variant="text" width="80%" />
 *   <Skeleton variant="text" width="60%" />
 *   <Skeleton variant="rectangular" width="100%" height="200px" />
 * </div>
 */
const Skeleton = ({ 
  variant = 'text',
  width = '100%',
  height,
  borderRadius,
  className = '',
  animation = 'wave',
  count = 1,
  spacing = '8px'
}) => {
  // Altura por defecto según la variante
  const defaultHeight = {
    text: '1em',
    circular: '40px',
    rectangular: '100px',
  };

  const finalHeight = height || defaultHeight[variant] || '20px';

  // Border radius por defecto según la variante
  const defaultBorderRadius = {
    text: '4px',
    circular: '50%',
    rectangular: '8px',
  };

  const finalBorderRadius = borderRadius || defaultBorderRadius[variant] || '4px';

  const skeletonStyle = {
    width,
    height: finalHeight,
    borderRadius: finalBorderRadius,
  };

  const skeletonClass = `
    loading-skeleton
    loading-skeleton--${variant}
    loading-skeleton--${animation}
    ${className}
  `.trim();

  // Si count > 1, renderizar múltiples skeletons con espaciado
  if (count > 1) {
    return (
      <div className="loading-skeleton-group" style={{ gap: spacing }}>
        {Array.from({ length: count }).map((_, index) => (
          <div 
            key={index}
            className={skeletonClass}
            style={skeletonStyle}
          >
            <div className="loading-skeleton__wave"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div 
      className={skeletonClass}
      style={skeletonStyle}
    >
      <div className="loading-skeleton__wave"></div>
    </div>
  );
};

Skeleton.propTypes = {
  /** Variante del skeleton que define su forma por defecto */
  variant: PropTypes.oneOf(['text', 'circular', 'rectangular']),
  /** Ancho del skeleton (cualquier valor CSS válido) */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Altura del skeleton (cualquier valor CSS válido) */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Border radius personalizado */
  borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Clase CSS adicional */
  className: PropTypes.string,
  /** Tipo de animación */
  animation: PropTypes.oneOf(['wave', 'pulse', 'none']),
  /** Número de skeletons a renderizar */
  count: PropTypes.number,
  /** Espaciado entre skeletons cuando count > 1 */
  spacing: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/**
 * SkeletonGroup - Contenedor para agrupar múltiples skeletons con layouts predefinidos
 */
export const SkeletonGroup = ({ children, layout = 'vertical', gap = '12px', className = '' }) => {
  const groupClass = `
    loading-skeleton-group
    loading-skeleton-group--${layout}
    ${className}
  `.trim();

  return (
    <div className={groupClass} style={{ gap }}>
      {children}
    </div>
  );
};

SkeletonGroup.propTypes = {
  children: PropTypes.node.isRequired,
  /** Layout del grupo: 'vertical', 'horizontal', 'grid' */
  layout: PropTypes.oneOf(['vertical', 'horizontal', 'grid']),
  /** Espaciado entre elementos */
  gap: PropTypes.string,
  /** Clase CSS adicional */
  className: PropTypes.string,
};

export default Skeleton;
